import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Sparkles, ThumbsUp, XCircle } from "lucide-react";
import { api } from "../lib/api";
import ClinqLogo from "../components/ClinqLogo";

const Ring = ({ score, color }) => {
  const r = 54, c = 2 * Math.PI * r, pct = Math.max(0, Math.min(100, score)) / 100;
  return (
    <svg viewBox="0 0 128 128" className="h-44 w-44" data-testid="verdict-ring">
      <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
      <circle cx="64" cy="64" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90 64 64)" style={{ transition: "stroke-dashoffset 1.2s ease-out" }} />
      <text x="64" y="60" textAnchor="middle" fill="#fff" fontSize="36" fontWeight="800" fontFamily="Manrope, sans-serif">{score}</text>
      <text x="64" y="80" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">out of 100</text>
    </svg>
  );
};

export default function Verdict() {
  const nav = useNavigate();
  const [fs, setFs] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/founder/free-score").then(({ data }) => setFs(data))
      .catch((e) => setErr(e?.response?.status === 404 ? "You haven't been scored yet." : "We couldn't load your score."));
  }, []);

  const toReport = () => nav("/onboarding?step=2", { replace: true });

  if (err) {
    return (
      <div className="min-h-screen bg-[#04302E] px-6 py-12 text-white">
        <p className="text-white/70" data-testid="verdict-error">{err}</p>
        <button onClick={toReport} className="mt-4 rounded-md bg-[#0A8B85] px-5 py-3 text-sm font-semibold">Back to my documents</button>
      </div>
    );
  }
  if (!fs) return <div className="flex min-h-screen items-center justify-center bg-[#04302E] text-white/70"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading your score…</div>;

  const likely = fs.likely;
  return (
    <div className="min-h-screen bg-[#04302E] text-white" data-testid="verdict-page">
      <div className="mx-auto max-w-3xl px-6 py-10 sm:px-10">
        <ClinqLogo />
        <p className="mt-10 text-[11px] font-bold uppercase tracking-[0.18em] text-white/50">Based on your documentation we have</p>
        <div className="mt-4 flex flex-col items-start gap-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:flex-row sm:items-center">
          <Ring score={fs.score} color={fs.band.color} />
          <div>
            <span className="inline-block rounded-full border px-3 py-1 text-xs font-extrabold uppercase tracking-wide"
              style={{ borderColor: fs.band.color, color: fs.band.color }} data-testid="verdict-band">{fs.band.label}</span>
            <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="verdict-heading">
              {likely ? <><ThumbsUp className="mr-2 inline h-8 w-8 text-emerald-300" />Funders on our platform are likely to fund your business.</>
                      : <><Sparkles className="mr-2 inline h-8 w-8 text-[#F59E0B]" />You're doing great — let's take you to the next step.</>}
            </h1>
            <p className="mt-3 text-sm text-white/60">
              {likely
                ? `${fs.likely_funders.join(", ")} are a possible fit for ${fs.purpose_label.toLowerCase()}.`
                : "Find out everything you're doing right so far, and what could take you to the next level and position you for the funders on our platform."}
              {" "}Indicative — based on documents received, not the full verified analysis.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="verdict-docs">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-400">Documentation · {fs.completeness_pct}% complete</p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {fs.have_docs.map((d) => <li key={d.id} className="flex items-center gap-2 text-white/80"><CheckCircle2 className="h-4 w-4 text-emerald-300" /> {d.label}</li>)}
              {fs.missing_docs.map((d) => <li key={d.id} className="flex items-center gap-2 text-white/50"><XCircle className="h-4 w-4 text-[#F59E0B]" /> {d.label} <span className="text-[10px] uppercase text-[#F59E0B]">missing</span></li>)}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="verdict-signals">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-400">What your documents show</p>
            {fs.signals.length === 0 ? (
              <p className="mt-3 text-sm text-white/60">We could not read financial figures from the documents yet — the full report extracts and verifies them.</p>
            ) : (
              <ul className="mt-3 space-y-1.5 text-sm">
                {fs.signals.map((s, i) => <li key={i} className="flex items-center justify-between gap-2"><span className="text-white/70">{s.label}</span><span className={s.positive ? "font-semibold text-emerald-300" : "font-semibold text-[#F59E0B]"}>{s.value}</span></li>)}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-6 text-center">
          <p className="text-base font-semibold text-white">
            {likely ? "Get your Capital Readiness Report and a free verification of your business from our Clinq advisors — then get connected to a funder on our platform."
                    : "See what you're doing right and what will position you for funders — get your Capital Readiness Report."}
          </p>
          <p className="mt-2 text-sm text-white/65">Full six-pillar analysis, verified figures, funder shortlist, gaps and a 90-day roadmap. Includes 3 re-runs as you improve.</p>
          <button onClick={toReport} data-testid="verdict-cta"
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#F59E0B] px-7 py-3.5 text-sm font-bold text-[#04302E] shadow-lg shadow-black/30 hover:bg-amber-400">
            Get my Capital Readiness Report <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
